import { apiClient } from './client';
import type { ApiResponse, PagedResult } from '@/types/api.types';
import type {
  UserDto,
  UserListDto,
  UserFilterDto,
  CreateUserRequest,
  UpdateUserRequest,
  ResetPasswordRequest,
  AssignRolesRequest,
  AssignLocationAccessRequest,
  AssignCostCenterAccessRequest,
  AssignCompanyAccessRequest,
  AssignableScopeDto,
} from '@/types/user.types';

export const userApi = {
  /** GET /api/v1/users — paginated list */
  /**
   * GET /users/assignable-scope - companies, their locations, and those locations' cost centres.
   *
   * Not the ordinary master lists. Those are scoped to the company on the caller's token, which is
   * right for someone working inside one, but the access screen grants across companies and could
   * only ever show the administrator's current one.
   */
  assignableScope: () =>
    apiClient.get<ApiResponse<AssignableScopeDto>>('/users/assignable-scope'),

  getList: (filter?: UserFilterDto) =>
    apiClient.get<ApiResponse<PagedResult<UserListDto>>>('/users', { params: filter }),

  /** GET /api/v1/users/{id} — full user with roles and access */
  getById: (id: string) =>
    apiClient.get<ApiResponse<UserDto>>(`/users/${id}`),

  /** POST /api/v1/users */
  create: (data: CreateUserRequest) =>
    apiClient.post<ApiResponse<{ id: string }>>('/users', data),

  /** PUT /api/v1/users/{id} */
  update: (id: string, data: UpdateUserRequest) =>
    apiClient.put<ApiResponse<object>>(`/users/${id}`, data),

  /** POST /api/v1/users/{id}/profile-image — multipart upload */
  uploadProfileImage: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post<ApiResponse<{ url: string }>>(
      `/users/${id}/profile-image`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },

  /** POST /api/v1/users/{id}/disable */
  disable: (id: string) =>
    apiClient.post<ApiResponse<object>>(`/users/${id}/disable`, {}),

  /** POST /api/v1/users/{id}/enable */
  enable: (id: string) =>
    apiClient.post<ApiResponse<object>>(`/users/${id}/enable`, {}),

  /** POST /api/v1/users/{id}/lock */
  lock: (id: string, reason?: string, lockoutMinutes?: number) =>
    apiClient.post<ApiResponse<object>>(`/users/${id}/lock`, {
      reason: reason ?? null,
      lockoutMinutes: lockoutMinutes ?? null,
    }),

  /** POST /api/v1/users/{id}/unlock */
  unlock: (id: string) =>
    apiClient.post<ApiResponse<object>>(`/users/${id}/unlock`, {}),

  /** POST /api/v1/users/{id}/reset-password */
  resetPassword: (id: string, data: ResetPasswordRequest) =>
    apiClient.post<ApiResponse<object>>(`/users/${id}/reset-password`, data),

  /** PUT /api/v1/users/{id}/roles */
  assignRoles: (id: string, data: AssignRolesRequest) =>
    apiClient.put<ApiResponse<object>>(`/users/${id}/roles`, data),

  /** PUT /api/v1/users/{id}/location-access */
  assignLocationAccess: (id: string, data: AssignLocationAccessRequest) =>
    apiClient.put<ApiResponse<object>>(`/users/${id}/location-access`, data),

  /** PUT /api/v1/users/{id}/cost-center-access */
  assignCostCenterAccess: (id: string, data: AssignCostCenterAccessRequest) =>
    apiClient.put<ApiResponse<object>>(`/users/${id}/cost-center-access`, data),

  /** PUT /api/v1/users/{id}/company-access */
  assignCompanyAccess: (id: string, data: AssignCompanyAccessRequest) =>
    apiClient.put<ApiResponse<object>>(`/users/${id}/company-access`, data),
};
